/**
 * Typed accessor for the generated MCP docs bundle.
 *
 * The JSON is produced by tools/build-mcp-docs-bundle.mjs and
 * committed at frontend/src/generated/mcp-docs-bundle.json. Import it once here so the
 * rest of the docs site gets a strongly-typed object without repeating the
 * relative path.
 */
import bundleRaw from '../../generated/mcp-docs-bundle.json';
import type { McpDocsBundle, McpDocsPrompt, McpDocsTool, McpDocsResource } from './types';

export const docsBundle = bundleRaw as unknown as McpDocsBundle;

export function findTool(name: string | undefined): McpDocsTool | undefined {
  if (!name) return undefined;
  return docsBundle.tools.find((t) => t.name === name);
}

export function findPrompt(name: string | undefined): McpDocsPrompt | undefined {
  if (!name) return undefined;
  return docsBundle.prompts.find((p) => p.name === name);
}

export function findResource(family: string | undefined): McpDocsResource | undefined {
  if (!family) return undefined;
  return docsBundle.resources.find((r) => r.family === family);
}

export function toolsForFamily(familyId: string): McpDocsTool[] {
  return docsBundle.tools.filter((t) => t.family_id === familyId);
}

export const MCP_ENDPOINT = 'https://beatbandit.ai/api/mcp';

export interface McpDocsToolGroup {
  id: string;
  title: string;
  tagline: string;
  tools: McpDocsTool[];
}

/**
 * Tools grouped by display family, in bundle family order. Tools whose
 * family_id has no family entry are collected into a trailing "Other" group so
 * every public tool is listed.
 */
export const docsToolGroups: McpDocsToolGroup[] = (() => {
  const groups: McpDocsToolGroup[] = docsBundle.families
    .map((family) => ({
      id: family.id,
      title: family.title,
      tagline: family.tagline,
      tools: toolsForFamily(family.id),
    }))
    .filter((group) => group.tools.length > 0);
  const known = new Set(docsBundle.families.map((family) => family.id));
  const orphans = docsBundle.tools.filter((tool) => !known.has(tool.family_id));
  if (orphans.length > 0) {
    groups.push({ id: 'other', title: 'Other', tagline: '', tools: orphans });
  }
  return groups;
})();

/** First sentence of a registry description, for index tables. */
export function summarize(text: string): string {
  const match = text.match(/^[^.!?]+[.!?]/);
  if (match) return match[0];
  if (text.length > 160) return text.slice(0, 157) + '...';
  return text;
}
